/* @flow */
import React, { Component } from 'react';
import { Row } from 'react-grid-system';
import { connect } from 'react-redux';
import {
  PageHeader,
  MenuOptionButton,
  PageSelector,
  OptionButtonGroup,
  LoadingIndicator,
  PromptText,
  MenuOptionsSearch,
  Images,
} from 'listertech-commons-web';

import SideNavPage from 'components/SideNavPage';
import PendingUsersTable from './PendingUsersTable';
import type { Paginator } from 'data/pagination';
import { PAGE_SIZE } from 'data/pagination/config';
import PaginationActions from 'data/pagination/actions';

import 'components/PaginationStyle/PaginationStyle.css';

type Props = {
  loading: boolean,
  error: string,
  all: any[],
  search: string,
  pagination: Paginator,
  getPendingUsers: (page: number, sort: string, desc: boolean, search: string) => void,
  setSearch: (search: string) => void,
  setActivePage: (key: string, page: number) => void,
  verifyUser: (id: string) => void,
  rejectUser: (id: string) => void,
};

class VerificationPage extends Component<Props> {
  componentDidMount() {
    this.fetchData(0, true);
  }

  handlePageChange = (page) => {
    this.fetchData(page - 1);
  };

  fetchData = (page: number, forceFetch: boolean) => {
    if (!Boolean(this.props.pagination.pages[page]) || forceFetch) {
      this.props.getPendingUsers(
        page,
        'createdAt',
        true,
        this.props.search,
      );
    } else {
      this.props.setActivePage('pendingVerification', page);
    }
  };

  handleSearchChange = (search: string) => {
    this.props.setSearch(search);
    this.fetchData(0, true);
  };

  handleApprove = (id: string) => {
    if (window.confirm('Are you sure you want to approve this user?')) {
      this.props.verifyUser(id);
    }
  };

  handleReject = (id: string) => {
    if (window.confirm('Are you sure you want to reject this user? This will remove their account.')) {
      this.props.rejectUser(id);
    }
  };

  render() {
    const paginator = this.props.pagination;
    const ids = paginator.pages[paginator.currentPage || 0] || [];
    const users = this.props.all.filter(u => ids.includes(u._id));

    return (
      <SideNavPage>
        <Row justify="space-between">
          <PageHeader>Verification</PageHeader>
          <OptionButtonGroup>
            <MenuOptionsSearch initialValue={this.props.search} onChange={this.handleSearchChange} />
          </OptionButtonGroup>
        </Row>
        {this.props.loading ? (
          <LoadingIndicator />
        ) : this.props.error ? (
          <PromptText>{this.props.error}</PromptText>
        ) : users.length === 0 ? (
          <PromptText>No pending verifications</PromptText>
        ) : (
          <PendingUsersTable
            data={users}
            onApprove={this.handleApprove}
            onReject={this.handleReject}
            onSort={() => {}}
          />
        )}
        <Row justify="flex-end" style={{ marginRight: 20 }}>
          {paginator.objectCount && paginator.pagesCount ? (
            <PageSelector
              total={paginator.objectCount}
              limit={PAGE_SIZE}
              pageCount={paginator.pagesCount}
              currentPage={paginator.currentPage + 1 || 1}
              onPageChange={this.handlePageChange}
            />
          ) : null}
        </Row>
      </SideNavPage>
    );
  }
}

const mapStateToProps = state => ({
  loading: state.loading['GET_PENDING_USERS'],
  error: state.error['GET_PENDING_USERS'],
  all: state.verification.all,
  search: state.verification.search,
  pagination: state.pagination.pendingVerification,
});

const mapDispatchToProps = dispatch => ({
  getPendingUsers: (page, sort, desc, search) =>
    dispatch({ type: 'GET_PENDING_USERS', page, sort, desc, search }),
  setSearch: search =>
    dispatch({ type: 'SET_VERIFICATION_SEARCH', search }),
  setActivePage: (key, page) =>
    dispatch(PaginationActions.setActivePage(key, page)),
  verifyUser: id =>
    dispatch({ type: 'VERIFY_USER', id }),
  rejectUser: id =>
    dispatch({ type: 'REJECT_USER', id }),
});

export default connect(mapStateToProps, mapDispatchToProps)(VerificationPage);
